<template>
  <div class="teacher-availability">
    <h4>Müsaitlik Takvimi</h4>

    <!-- Hafta İçi Günleri -->
    <div class="weekday-section">
      <h5>Hafta İçi</h5>
      <div class="days-container" ref="weekdayScrollEl">
        <div v-for="day in weekdays" :key="day.key" class="day-row">
          <div class="day-label">{{ day.name }}</div>
          <div class="time-slots">
            <div
              v-for="slot in weekdaySlots"
              :key="slot.id"
              :class="[
                'time-slot',
                {
                  'selected': isSlotSelected(day.key, slot.id),
                  'disabled': slot.disabled,
                  'lunch-break': slot.disabled
                }
              ]"
              @click="slot.disabled ? null : toggleSlot(day.key, slot.id)"
            >
              <span class="time-text">{{ slot.time }}</span>
              <span v-if="slot.disabled" class="break-text">Öğle Arası</span>
            </div>
          </div>
        </div>
      </div>
      <div v-show="weekdayTrackVisible" class="custom-hscroll" ref="weekdayTrackEl" @click="handleWeekdayTrackClick">
        <div class="custom-hscroll-thumb"
             :style="{ transform: `translateX(${weekdayThumbLeft}px)`, width: weekdayThumbWidth + 'px' }"
             @mousedown="startWeekdayDrag"></div>
      </div>

    <!-- Hafta Sonu Günleri -->
    <div class="weekend-section">
      <h5>Hafta Sonu</h5>
      <div class="days-container" ref="weekendScrollEl">
        <div v-for="day in weekends" :key="day.key" class="day-row">
          <div class="day-label">{{ day.name }}</div>
          <div class="time-slots">
            <div
              v-for="slot in weekendSlots"
              :key="slot.id"
              :class="[
                'time-slot',
                {
                  'selected': isSlotSelected(day.key, slot.id),
                  'disabled': slot.disabled,
                  'lunch-break': slot.disabled
                }
              ]"
              @click="slot.disabled ? null : toggleSlot(day.key, slot.id)"
            >
              <span class="time-text">{{ slot.time }}</span>
              <span v-if="slot.disabled" class="break-text">Öğle Arası</span>
            </div>
          </div>
        </div>
      </div>
      <div v-show="weekendTrackVisible" class="custom-hscroll" ref="weekendTrackEl" @click="handleWeekendTrackClick">
        <div class="custom-hscroll-thumb"
             :style="{ transform: `translateX(${weekendThumbLeft}px)`, width: weekendThumbWidth + 'px' }"
             @mousedown="startWeekendDrag"></div>
      </div>
    </div>

    <!-- Seçilen Saatler Özeti -->
    <div class="selected-summary" v-if="getSelectedSlotsCount() > 0">
      <h6>Seçilen Saatler: {{ getSelectedSlotsCount() }}</h6>
      <div class="selected-slots-list">
        <span 
          v-for="(slots, day) in selectedSlots" 
          :key="day"
          class="day-selection"
        >
          <strong>{{ getDayName(day) }}:</strong> 
          {{ slots.length }} saat
        </span>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, watch, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { generateWeekdaySlots, generateWeekendSlots } from '@/utils/slotGenerator'
import { useHorizontalScrollbar } from '@/composables/useHorizontalScrollbar'

export default {
  name: 'TeacherAvailability',
  props: {
    modelValue: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const store = useStore()
    const selectedSlots = ref({})

    const weekdays = [
      { key: 'monday', name: 'Pazartesi' },
      { key: 'tuesday', name: 'Salı' },
      { key: 'wednesday', name: 'Çarşamba' },
      { key: 'thursday', name: 'Perşembe' }
    ]

    const weekends = [
      { key: 'saturday', name: 'Cumartesi' },
      { key: 'sunday', name: 'Pazar' }
    ]

    // Slot ayarları store'dan gelir, mount'ta yüklenir (henüz yüklenmediyse fetch)
    const settings = computed(() => store.getters.getSlotSettings)
    const weekdaySlots = computed(() => generateWeekdaySlots(settings.value))
    const weekendSlots = computed(() => generateWeekendSlots(settings.value))

    // Custom horizontal scrollbar — macOS overlay davranışını atlar
    const weekdayScroll = useHorizontalScrollbar()
    const weekendScroll = useHorizontalScrollbar()

    // Slot dizisi değişince (settings güncelenince) scrollbar thumb'ı yeniden hesapla
    watch(() => [weekdaySlots.value, weekendSlots.value], () => {
      weekdayScroll.refresh()
      weekendScroll.refresh()
    })

    onMounted(() => {
      if (!settings.value) {
        store.dispatch('fetchSlotSettings')
      }
    })

    const getDayName = (dayKey) => {
      const allDays = [...weekdays, ...weekends]
      const day = allDays.find(d => d.key === dayKey)
      return day ? day.name : dayKey
    }

    const toggleSlot = (dayKey, slotId) => {
      if (!selectedSlots.value[dayKey]) {
        selectedSlots.value[dayKey] = []
      }

      const index = selectedSlots.value[dayKey].indexOf(slotId)
      if (index > -1) {
        selectedSlots.value[dayKey].splice(index, 1)
        if (selectedSlots.value[dayKey].length === 0) {
          delete selectedSlots.value[dayKey]
        }
      } else {
        selectedSlots.value[dayKey].push(slotId)
      }

      emit('update:modelValue', { ...selectedSlots.value })
    }

    const isSlotSelected = (dayKey, slotId) => {
      return selectedSlots.value[dayKey] && selectedSlots.value[dayKey].includes(slotId)
    }

    const getSelectedSlotsCount = () => {
      return Object.values(selectedSlots.value).reduce((total, slots) => total + slots.length, 0)
    }

    const initializeFromProps = () => {
      if (props.modelValue && Object.keys(props.modelValue).length > 0) {
        selectedSlots.value = { ...props.modelValue }

      }
    }

    initializeFromProps()

    watch(() => props.modelValue, (newValue) => {
      if (newValue && Object.keys(newValue).length > 0) {
        selectedSlots.value = { ...newValue }

      } else {
        selectedSlots.value = {}

      }
    }, { deep: true, immediate: true })

    return {
      weekdays,
      weekends,
      weekdaySlots,
      weekendSlots,
      selectedSlots,
      toggleSlot,
      isSlotSelected,
      getSelectedSlotsCount,
      getDayName,
      // Custom scrollbar — Hafta İçi
      weekdayScrollEl: weekdayScroll.scrollEl,
      weekdayTrackEl: weekdayScroll.trackEl,
      weekdayThumbLeft: weekdayScroll.thumbLeft,
      weekdayThumbWidth: weekdayScroll.thumbWidth,
      weekdayTrackVisible: weekdayScroll.trackVisible,
      startWeekdayDrag: weekdayScroll.startDrag,
      handleWeekdayTrackClick: weekdayScroll.handleTrackClick,
      // Custom scrollbar — Hafta Sonu
      weekendScrollEl: weekendScroll.scrollEl,
      weekendTrackEl: weekendScroll.trackEl,
      weekendThumbLeft: weekendScroll.thumbLeft,
      weekendThumbWidth: weekendScroll.thumbWidth,
      weekendTrackVisible: weekendScroll.trackVisible,
      startWeekendDrag: weekendScroll.startDrag,
      handleWeekendTrackClick: weekendScroll.handleTrackClick
    }
  }
}
</script>

<style scoped>
.teacher-availability {
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 1.5rem;
  overflow-x: hidden;
  width: 100%;
}

.teacher-availability h4 {
  margin: 0 0 1.5rem 0;
  color: #333;
  font-size: 1.2rem;
  border-bottom: 2px solid #4CAF50;
  padding-bottom: 0.5rem;
}

.teacher-availability h5 {
  margin: 1rem 0 0.75rem 0;
  color: #555;
  font-size: 1rem;
  font-weight: 600;
}

.weekday-section, .weekend-section {
  margin-bottom: 2rem;
}

.days-container {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;
  /* Native scrollbar'ı gizle — custom bar aşağıda görünür */
  scrollbar-width: none; /* Firefox */
}
.days-container::-webkit-scrollbar {
  display: none; /* Chrome/Safari/Edge */
}

/* Custom Yatay Scrollbar — HER ZAMAN görünür (macOS overlay bug fix) */
.custom-hscroll {
  height: 14px;
  background: #e0e0e0;
  border-radius: 7px;
  position: relative;
  margin: 8px 0 4px 0;
  cursor: pointer;
  box-shadow: inset 0 0 4px rgba(0, 0, 0, 0.12);
  user-select: none;
}

.custom-hscroll-thumb {
  position: absolute;
  top: 2px;
  bottom: 2px;
  left: 0;
  background: linear-gradient(90deg, #4CAF50, #45a049);
  border-radius: 5px;
  cursor: grab;
  transition: background 0.2s ease;
  will-change: transform, width;
}

.custom-hscroll-thumb:hover {
  background: linear-gradient(90deg, #45a049, #388e3c);
}

.custom-hscroll-thumb:active {
  cursor: grabbing;
  background: linear-gradient(90deg, #388e3c, #2e7d32);
}

.day-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f0f0f0;
  min-width: max-content;
}

.day-row:last-child {
  border-bottom: none;
}

.day-label {
  min-width: 100px;
  font-weight: 600;
  color: #333;
  font-size: 0.9rem;
}

.time-slots {
  display: flex;
  gap: 0.5rem;
  overflow-x: visible;
  padding: 0.25rem 0;
  flex: 1;
  justify-content: flex-start;
  align-items: center;
  min-width: max-content;
  width: max-content;
}

.time-slot {
  min-width: 120px;
  height: 40px;
  border: 2px solid #e0e0e0;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: white;
  position: relative;
  flex-shrink: 0;
}



.time-slot:hover:not(.disabled) {
  border-color: #4CAF50;
  background-color: #f8fff8;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(76, 175, 80, 0.2);
}

.time-slot.selected {
  border-color: #4CAF50;
  background-color: #4CAF50;
  color: white;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(76, 175, 80, 0.3);
}

.time-slot.disabled {
  background-color: #f5f5f5;
  border-color: #ddd;
  color: #999;
  cursor: not-allowed;
  opacity: 0.6;
}

.time-slot.lunch-break {
  background-color: #fff3cd;
  border-color: #ffeaa7;
  color: #856404;
}

.time-text {
  font-size: 0.8rem;
  font-weight: 500;
  text-align: center;
}

.break-text {
  font-size: 0.7rem;
  font-weight: 600;
  text-align: center;
}

.selected-summary {
  margin-top: 1.5rem;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 6px;
  border-left: 4px solid #4CAF50;
}

.selected-summary h6 {
  margin: 0 0 0.75rem 0;
  color: #333;
  font-size: 0.9rem;
}

.selected-slots-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.day-selection {
  background: white;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.8rem;
  color: #666;
  border: 1px solid #e0e0e0;
}

.time-slots::-webkit-scrollbar {
  height: 8px;
}

.time-slots::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.time-slots::-webkit-scrollbar-thumb {
  background: #4CAF50;
  border-radius: 4px;
}

.time-slots::-webkit-scrollbar-thumb:hover {
  background: #45a049;
}

@media (max-width: 768px) {
  .teacher-availability {
    padding: 1rem;
    overflow-x: auto;
  }

  .day-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
    width: 100%;
  }

  .day-label {
    min-width: auto;
    font-size: 0.85rem;
  }

  .time-slots {
    width: 100%;
    justify-content: flex-start;
    align-items: center;
  }

  .time-slot {
    min-width: 100px;
    height: 35px;
  }

  .time-text {
    font-size: 0.75rem;
  }

  .break-text {
    font-size: 0.65rem;
  }
}

@media (max-width: 480px) {
  .time-slot {
    min-width: 90px;
    height: 32px;
  }

  .time-text {
    font-size: 0.7rem;
  }

  .selected-slots-list {
    flex-direction: column;
    gap: 0.5rem;
  }
}
</style>
